/**
 * ConditionNode - Node component for 'condition' type fields
 * 
 * Displays conditional branching based on variables.
 * Each condition option has its own source handle.
 */

import React, { memo, useState, useCallback, useEffect } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import IconProvider from '@/components/ui/IconProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/InputDefault';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/DefaultSelectDropdown';
import { DialogField, ConditionOption } from '@/lib/stores/dialogStore';
import { BaseDialogNode } from './BaseDialogNode';
import { DialogFieldNodeData, getFieldTypeIcon, getFieldTypeColor, getSectionName } from './dialogNodeUtils';
import { getTypeItemById } from '@/lib/utils/typeStoreResolver';

export const ConditionNode = memo(({ id, data, selected }: NodeProps<any>) => {
  const nodeData = data as DialogFieldNodeData;
  const field = nodeData.field;
  const [isEditing, setIsEditing] = useState(false);
  const [editedType, setEditedType] = useState(field.type);
  const [editedVariable, setEditedVariable] = useState(field.condition?.variable || '');
  const [editedCheckVariable, setEditedCheckVariable] = useState(field.condition?.checkVariable || '');
  const [editedConditionOptions, setEditedConditionOptions] = useState<ConditionOption[]>(field.conditionOptions || []);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const prevSelectedRef = React.useRef(selected);

  // Get the selected variable's type (for edit mode)
  const selectedVariable = nodeData.variables?.find(v => v.name === editedVariable);
  const selectedVariableType = selectedVariable?.type;
  const isBoolean = selectedVariableType === 'boolean';
  const isText = selectedVariableType === 'text';
  const isRange = selectedVariableType === 'range';
  const isRangeSelector = selectedVariableType === 'range-selector';
  const isNumber = selectedVariableType === 'number' || isRange;
  
  // Get the variable type for view mode (based on saved condition)
  const viewVariable = nodeData.variables?.find(v => v.name === field.condition?.variable);
  const viewVariableType = viewVariable?.type;
  const viewIsBoolean = viewVariableType === 'boolean';
  const viewIsText = viewVariableType === 'text';
  const viewIsRangeSelector = viewVariableType === 'range-selector';

  // Attach native event listeners to prevent dragging on all inputs and selects
  useEffect(() => {
    const content = contentRef.current;
    if (!content || !isEditing) return;

    const handlePointerDown = (e: PointerEvent) => {
      // Only prevent if target is an input, select, or textarea element
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA' || 
          target.closest('input') || target.closest('select') || target.closest('textarea') ||
          target.closest('[role="combobox"]')) {
        e.stopImmediatePropagation();
      }
    };

    const handleMouseDown = (e: MouseEvent) => {
      // Only prevent if target is an input, select, or textarea element
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA' || 
          target.closest('input') || target.closest('select') || target.closest('textarea') ||
          target.closest('[role="combobox"]')) {
        e.stopImmediatePropagation();
      }
    };

    // Use capture phase to run before React Flow's listeners
    content.addEventListener('pointerdown', handlePointerDown, { capture: true });
    content.addEventListener('mousedown', handleMouseDown, { capture: true });
    
    return () => {
      content.removeEventListener('pointerdown', handlePointerDown, { capture: true });
      content.removeEventListener('mousedown', handleMouseDown, { capture: true });
    };
  }, [isEditing]);

  // Sync local state with prop changes when not editing
  useEffect(() => {
    if (!isEditing) {
      setEditedType(field.type);
      setEditedVariable(field.condition?.variable || '');
      setEditedCheckVariable(field.condition?.checkVariable || '');
      setEditedConditionOptions(field.conditionOptions || []);
    }
  }, [field, isEditing]);

  // When variable changes, auto-setup boolean/range-selector options if needed
  const handleVariableChange = useCallback((newVariable: string) => {
    setEditedVariable(newVariable);
    const variable = nodeData.variables?.find(v => v.name === newVariable);
    const variableType = variable?.type;
    
    if (variableType === 'boolean') {
      // Auto-create True/False options for boolean variables
      setEditedConditionOptions([
        { id: 'bool-true', condition_value: true, operator: '==', order_index: 0 },
        { id: 'bool-false', condition_value: false, operator: '==', order_index: 1 },
      ]);
    } else if (variableType === 'range-selector' && variable?.typeStoreRef?.typeId) {
      // Auto-create range options from TypeItem
      import('@/lib/utils/typeStoreResolver').then(({ getTypeItemById }) => {
        const typeItem = getTypeItemById(variable.typeStoreRef!.typeId);
        if (typeItem?.data) {
          const rangeElements = typeItem.data.filter(el => el.valueType === 'range');
          const rangeOptions = rangeElements.map((el, idx) => ({
            id: `range-${el.id}`,
            condition_value: el.id,
            operator: 'in_range' as const,
            order_index: idx
          }));
          setEditedConditionOptions(rangeOptions);
        }
      });
    } else if (editedConditionOptions.length === 2 && 
               editedConditionOptions.some(o => o.condition_value === true) && 
               editedConditionOptions.some(o => o.condition_value === false)) {
      // Clear boolean options when switching to non-boolean
      setEditedConditionOptions([]);
    }
  }, [nodeData.variables, editedConditionOptions]);

  const handleSaveChanges = useCallback(() => {
    if (nodeData.onFieldUpdate) {
      const updates: Partial<DialogField> = {
        type: editedType,
      };
      
      // Include condition and conditionOptions if it's a condition type
      if (editedType === 'condition') {
        updates.condition = { 
          variable: editedVariable,
          ...(editedCheckVariable && { checkVariable: editedCheckVariable })
        };
        updates.conditionOptions = editedConditionOptions;
      }
      
      nodeData.onFieldUpdate(nodeData.nodeIndex, nodeData.fieldIndex, updates);
    }
    setIsEditing(false);
  }, [editedType, editedVariable, editedCheckVariable, editedConditionOptions, nodeData]);

  // Auto-save when node loses selection (clicking outside)
  useEffect(() => {
    // Only auto-save if we're transitioning from selected to unselected while editing
    if (prevSelectedRef.current && !selected && isEditing) {
      handleSaveChanges();
    }
    // Update ref for next render
    prevSelectedRef.current = selected;
  }, [selected, isEditing, handleSaveChanges]);

  const handleEditToggle = useCallback(() => {
    if (isEditing) {
      handleSaveChanges();
    } else {
      setIsEditing(true);
      setEditedType(field.type);
      setEditedVariable(field.condition?.variable || '');
      setEditedCheckVariable(field.condition?.checkVariable || '');
      setEditedConditionOptions(field.conditionOptions || []);
    }
  }, [isEditing, handleSaveChanges, field]);

  const handleAddConditionOption = useCallback(() => {
    const newCondOption: ConditionOption = {
      id: `temp-${Date.now()}`,
      condition_value: 0,
      operator: '==',
      order_index: editedConditionOptions.length,
    };
    setEditedConditionOptions([...editedConditionOptions, newCondOption]);
  }, [editedConditionOptions]);

  const handleUpdateConditionOption = useCallback((index: number, updates: Partial<ConditionOption>) => {
    const updated = [...editedConditionOptions];
    updated[index] = { ...updated[index], ...updates };
    setEditedConditionOptions(updated);
  }, [editedConditionOptions]);

  const handleRemoveConditionOption = useCallback((index: number) => {
    setEditedConditionOptions(editedConditionOptions.filter((_, i) => i !== index));
  }, [editedConditionOptions]);

  const colors = getFieldTypeColor(isEditing ? editedType : field.type);

  // Header content for edit mode (with type selector)
  const headerContent = isEditing ? (
    <>
      <IconProvider iconstring={getFieldTypeIcon(editedType)} size={14} className={colors.text} />
      <Select value={editedType} onValueChange={(value: any) => setEditedType(value)}>
        <SelectTrigger className="h-6 text-xs flex-1 bg-white noDrag">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="dialog">Dialog</SelectItem>
          <SelectItem value="question">Question</SelectItem>
          <SelectItem value="context">Context</SelectItem>
          <SelectItem value="condition">Condition</SelectItem>
          <SelectItem value="end-dialog">End Dialog</SelectItem>
        </SelectContent>
      </Select>
    </>
  ) : undefined;

  return (
    <BaseDialogNode
      id={id}
      data={nodeData}
      selected={selected}
      isEditing={isEditing}
      onEditToggle={handleEditToggle}
      headerContent={headerContent}
    >
      <div ref={contentRef}>
        {/* Condition options editing - when in edit mode */}
        {isEditing && (
        <div className="mb-2">
          {/* Variable selector */}
          <div className="flex items-center gap-2 mb-2 p-2 bg-orange-50 border border-orange-200 rounded">
            <IconProvider iconstring="fa-code" size={12} className="text-orange-700 flex-shrink-0" />
            <span className="text-xs font-semibold text-orange-800 flex-shrink-0">Variable:</span>
            {nodeData.variables && nodeData.variables.length > 0 ? (
              <Select value={editedVariable} onValueChange={handleVariableChange}>
                <SelectTrigger className="h-6 text-xs flex-1 bg-white font-mono noDrag">
                  <SelectValue placeholder="Select variable..." />
                </SelectTrigger>
                <SelectContent>
                  {nodeData.variables.map((variable) => (
                    <SelectItem key={variable.name} value={variable.name}>
                      <span className="font-mono">{variable.name}</span>
                      <span className="text-muted-foreground ml-2">({variable.type})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <span className="text-xs text-gray-500 italic">No variables defined in dialog</span>
            )}
          </div>
          
          {/* Check Variable selector (for range or range-selector type) */}
          {(isRange || isRangeSelector) && (
            <div className="flex items-center gap-2 mb-2 p-2 bg-purple-50 border border-purple-200 rounded">
              <IconProvider iconstring="fa-crosshairs" size={12} className="text-purple-700 flex-shrink-0" />
              <span className="text-xs font-semibold text-purple-800 flex-shrink-0">Check Variable:</span>
              {nodeData.variables && nodeData.variables.length > 0 ? (
                <Select value={editedCheckVariable} onValueChange={setEditedCheckVariable}>
                  <SelectTrigger className="h-6 text-xs flex-1 bg-white font-mono noDrag">
                    <SelectValue placeholder="Select variable to check..." />
                  </SelectTrigger>
                  <SelectContent>
                    {nodeData.variables
                      .filter(v => v.type === 'number' || v.type === 'range')
                      .map((variable) => (
                        <SelectItem key={variable.name} value={variable.name}>
                          <span className="font-mono">{variable.name}</span>
                          <span className="text-muted-foreground ml-2">({variable.type})</span>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              ) : (
                <span className="text-xs text-gray-500 italic">No numeric variables available</span>
              )}
            </div>
          )}
          
          {/* Condition options list (editable) */}
          <div className="space-y-2">
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs font-semibold text-orange-700">
                {isBoolean ? 'Boolean Branches:' : 'Condition Options:'}
              </div>
              {!isBoolean && (
                <Button
                  onClick={handleAddConditionOption}
                  size="sm"
                  variant="ghost"
                  className="h-6 px-2 text-xs"
                >
                  <IconProvider iconstring="fa-plus" size={10} className="mr-1" />
                  Add
                </Button>
              )}
            </div>
            
            {isBoolean ? (
              /* Boolean variable - show fixed True/False options */
              <div className="space-y-2">
                <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-300 rounded">
                  <IconProvider iconstring="fa-check" size={12} className="text-green-700" />
                  <span className="text-xs font-semibold text-green-800 flex-1">True</span>
                </div>
                <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-300 rounded">
                  <IconProvider iconstring="fa-times" size={12} className="text-red-700" />
                  <span className="text-xs font-semibold text-red-800 flex-1">False</span>
                </div>
              </div>
            ) : isRangeSelector ? (
              /* Range selector - show range elements from TypeItem */
              (() => {
                if (!selectedVariable?.typeStoreRef?.typeId) {
                  return <div className="text-xs text-gray-500 italic">No ranges configured</div>;
                }
                
                const typeItem = getTypeItemById(selectedVariable.typeStoreRef.typeId);
                const ranges = typeItem?.data?.filter(el => el.valueType === 'range') || [];
                
                return ranges.length > 0 ? (
                  <div className="space-y-2">
                    {ranges.map((range, idx) => (
                      <div 
                        key={range.id}
                        className="flex items-center gap-2 p-2 bg-indigo-50 border border-indigo-200 rounded"
                      >
                        <IconProvider iconstring="fa-layer-group" size={12} className="text-indigo-700" />
                        <span className="text-xs font-semibold text-indigo-800 flex-1">
                          {range.name} ({range.rangeMin ?? 0} - {range.rangeMax ?? 100})
                        </span>
                      </div>
                    ))}
                  </div>
                ) : <div className="text-xs text-gray-500 italic">No ranges defined in TypeItem</div>;
              })()
            ) : (
              /* Text/Number/Range variable - show customizable options */
              editedConditionOptions.map((condOption, idx) => (
                <div 
                  key={condOption.id || idx}
                  className="flex items-center gap-2 p-2 bg-orange-50 border border-orange-200 rounded"
                >
                  <div className="flex-1 grid grid-cols-2 gap-2">
                    {/* Operator */}
                    <Select 
                      value={condOption.operator || '=='} 
                      onValueChange={(value: any) => handleUpdateConditionOption(idx, { operator: value })}
                    >
                      <SelectTrigger className="h-7 text-xs noDrag">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="==">==</SelectItem>
                        <SelectItem value="!=">!=</SelectItem>
                        {isText ? (
                          /* Text-specific operators */
                          <>
                            <SelectItem value="contains">contains</SelectItem>
                            <SelectItem value="startsWith">starts with</SelectItem>
                            <SelectItem value="endsWith">ends with</SelectItem>
                          </>
                        ) : (
                          /* Numeric operators */
                          <>
                            <SelectItem value=">">&gt;</SelectItem>
                            <SelectItem value="<">&lt;</SelectItem>
                            <SelectItem value=">=">&gt;=</SelectItem>
                            <SelectItem value="<=">&lt;=</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                    
                    {/* Value */}
                    <Input
                      type={isText ? "text" : "number"}
                      value={String(condOption.condition_value)}
                      onChange={(e) => {
                        let newValue: string | number | boolean = e.target.value;
                        if (!isText) {
                          // For numeric types, parse as number
                          newValue = parseFloat(e.target.value) || 0;
                        }
                        handleUpdateConditionOption(idx, { condition_value: newValue as any });
                      }}
                      className="h-7 text-xs noDrag"
                      placeholder={isText ? "Text value" : "Number"}
                    />
                  </div>
                  
                  <button
                    onClick={() => handleRemoveConditionOption(idx)}
                    className="hover:bg-red-100 p-1 rounded transition-colors"
                  >
                    <IconProvider iconstring="fa-trash" size={10} className="text-red-600" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Condition formula display - View Mode */}
      {!isEditing && field.condition && (
        <div className="mb-2">
          {/* Variable being checked */}
          <div className="flex items-center gap-2 mb-2 p-2 bg-orange-50 border border-orange-200 rounded">
            <IconProvider iconstring="fa-code" size={12} className="text-orange-700" />
            <span className="text-xs font-semibold text-orange-800">
              Variable: <span className="font-mono">{field.condition.variable || 'Not configured'}</span>
            </span>
          </div>
          
          {/* Check Variable display (for range or range-selector type) */}
          {(viewVariable?.type === 'range' || viewVariable?.type === 'range-selector') && field.condition.checkVariable && (
            <div className="flex items-center gap-2 mb-2 p-2 bg-purple-50 border border-purple-200 rounded">
              <IconProvider iconstring="fa-crosshairs" size={12} className="text-purple-700" />
              <span className="text-xs font-semibold text-purple-800">
                Check Variable: <span className="font-mono">{field.condition.checkVariable}</span>
              </span>
            </div>
          )}

          {/* Condition Options - displayed as options/branches */}
          <div className="space-y-1.5">
            <div className="text-xs font-semibold text-orange-700 text-center mb-1">
              {viewIsBoolean ? 'Branches:' : 'Options:'}
            </div>
            
            {field.conditionOptions && field.conditionOptions.length > 0 ? (
              field.conditionOptions.map((condOption, idx) => {
                const isExternalJump = !!condOption.next_dialog;
                const hasDestination = condOption.next_section || condOption.next_dialog;
                const isTrueOption = condOption.condition_value === true;
                const isFalseOption = condOption.condition_value === false;
                
                // Boolean-specific styling
                const boolColors = viewIsBoolean 
                  ? (isTrueOption 
                      ? 'bg-green-50 hover:bg-green-100 border-green-300' 
                      : 'bg-red-50 hover:bg-red-100 border-red-300')
                  : '';
                
                // Get display value for range-selector types
                const getConditionValueDisplay = () => {
                  if (viewIsBoolean) {
                    return isTrueOption ? 'True' : 'False';
                  }
                  
                  // For range-selector with in_range operator, show the range name
                  if (viewIsRangeSelector && condOption.operator === 'in_range' && viewVariable?.typeStoreRef?.typeId) {
                    const typeItem = getTypeItemById(viewVariable.typeStoreRef.typeId);
                    const rangeElement = typeItem?.data?.find(el => el.id === String(condOption.condition_value));
                    if (rangeElement) {
                      return `in ${rangeElement.name}`;
                    }
                  }
                  
                  return `${condOption.operator || '=='} ${String(condOption.condition_value)}`;
                };
                
                return (
                  <div
                    key={condOption.id || idx}
                    className={`flex items-start gap-2 text-xs p-2 rounded border transition-colors relative ${
                      viewIsBoolean
                        ? boolColors
                        : isExternalJump 
                          ? 'bg-orange-50 hover:bg-orange-100 border-orange-300' 
                          : hasDestination
                          ? 'bg-purple-50 hover:bg-purple-100 border-purple-300'
                          : 'bg-gray-50 hover:bg-gray-100 border-gray-300'
                    }`}
                  >
                    <IconProvider 
                      iconstring={viewIsBoolean 
                        ? (isTrueOption ? "fa-check" : "fa-times")
                        : (isExternalJump ? "fa-arrow-up-right-from-square" : "fa-arrow-right")} 
                      size={10} 
                      className={`mt-0.5 flex-shrink-0 ${
                        viewIsBoolean
                          ? (isTrueOption ? 'text-green-600' : 'text-red-600')
                          : (isExternalJump ? 'text-orange-600' : hasDestination ? 'text-purple-600' : 'text-gray-400')
                      }`}
                    />
                    <div className="flex-1 space-y-0.5">
                      {/* Condition value display */}
                      <div className={`font-mono ${viewIsBoolean ? (isTrueOption ? 'text-green-900' : 'text-red-900') : 'text-orange-900'}`}>
                        {getConditionValueDisplay()}
                      </div>
                      
                    </div>
                    
                    {/* Handle for this option */}
                    <Handle 
                      type="source" 
                      position={Position.Right}
                      id={`cond-option-${idx}`}
                      className={`!absolute !right-[-6px] !top-1/2 !-translate-y-1/2 w-3 h-3 transition-colors ${
                        viewIsBoolean
                          ? (isTrueOption ? '!bg-green-500 hover:!bg-green-600' : '!bg-red-500 hover:!bg-red-600')
                          : isExternalJump 
                            ? '!bg-orange-500 hover:!bg-orange-600' 
                            : hasDestination
                            ? '!bg-purple-500 hover:!bg-purple-600'
                            : '!bg-gray-400'
                      }`}
                      style={{ position: 'absolute' }}
                    />
                  </div>
                );
              })
            ) : (
              <div className="text-xs text-gray-500 italic text-center py-2">
                No condition options configured
              </div>
            )}
          </div>
        </div>
      )}

      {/* Metadata badges */}
      {!isEditing && field.translatable && (
        <div className="flex items-center gap-2 mt-2 text-xs">
          <div className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
            <IconProvider iconstring="fa-language" size={10} />
            <span>Translatable</span>
          </div>
        </div>
      )}
      </div>
    </BaseDialogNode>
  );
});

ConditionNode.displayName = 'ConditionNode';

export default ConditionNode;
